import { observer } from 'mobx-react-lite';
import { useCapacityStore } from '../store/CapacityPlannerContext';
import CPSummaryCards from './CPSummaryCards';
import CPPlanningParamsPanel from './CPPlanningParamsPanel';
import CPHeadcountGrid from './CPHeadcountGrid';
import CPTrendChart from './CPTrendChart';
import CPHeadcountBreakdown from './CPHeadcountBreakdown';
import CPRoomUtilization from './CPRoomUtilization';
import CPForecastJobsChart from './CPForecastJobsChart';
import CPCapacityDataGrid from './CPCapacityDataGrid';
import styles from './cpDashboard.module.scss';

const CPDashboard = observer(() => {
  const store = useCapacityStore();

  // ---- Loading state -------------------------------------------------------
  if (store.isDashboardLoading) {
    return (
      <div className={styles.loading}>
        <i className={`pi pi-spin pi-spinner ${styles.loadingSpinner}`} />
        <p className={styles.loadingText}>Loading capacity data...</p>
      </div>
    );
  }

  // ---- Error state ---------------------------------------------------------
  if (store.dashboardError) {
    return (
      <div className={styles.error}>
        <i className={`pi pi-exclamation-triangle ${styles.errorIcon}`} />
        <p className={styles.errorTitle}>Error loading dashboard</p>
        <p className={styles.errorMessage}>{store.dashboardError}</p>
        <button
          className={styles.retryButton}
          onClick={() => store.fetchDashboard()}
          type="button"
        >
          Retry
        </button>
      </div>
    );
  }

  // ---- Empty state (new/blank dashboard) ----------------------------------
  if (!store.dashboardData) {
    return (
      <div className={styles.dashboardGrid}>
        <CPPlanningParamsPanel />
        <div className={styles.emptyPrompt}>
          <i className="pi pi-sliders-h" style={{ fontSize: 32, color: '#94a3b8' }} />
          <p>Set your planning parameters above and click <strong>Apply &amp; Recalculate</strong> to generate the dashboard.</p>
        </div>
      </div>
    );
  }

  const { months } = store.dashboardData;
  const filteredMonths = store.filteredMonths;
  const effectiveMonth = filteredMonths[0] ?? months[0];

  return (
    <div className={styles.dashboardGrid}>
      {/* ------------------------------------------------------------------ */}
      {/* Planning parameters panel                                           */}
      {/* ------------------------------------------------------------------ */}
      <CPPlanningParamsPanel />

      {/* ------------------------------------------------------------------ */}
      {/* Summary cards                                                       */}
      {/* ------------------------------------------------------------------ */}
      <CPSummaryCards selectedMonth={effectiveMonth} />

      {/* ------------------------------------------------------------------ */}
      {/* Headcount plan grid                                                 */}
      {/* ------------------------------------------------------------------ */}
      <CPHeadcountGrid selectedMonth={effectiveMonth} />

      {/* ------------------------------------------------------------------ */}
      {/* Charts row: trend + HC breakdown                                    */}
      {/* ------------------------------------------------------------------ */}
      <div className={styles.chartsRow}>
        <CPTrendChart />
        <CPHeadcountBreakdown selectedMonth={effectiveMonth} />
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Half row: room utilization + forecast jobs                          */}
      {/* ------------------------------------------------------------------ */}
      <div className={styles.halfRow}>
        <CPRoomUtilization selectedMonth={effectiveMonth} />
        <CPForecastJobsChart />
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Full-width data grids (crewing factors, packaging/quality FTE etc.) */}
      {/* ------------------------------------------------------------------ */}
      <CPCapacityDataGrid />
    </div>
  );
});

export default CPDashboard;
